import type { SmBettingPolicy } from "@bridge-applications/pb-types";
import { useCallback, useEffect, useState } from "react";
import betSizeChangeSound from "../../assets/static/audio/slot/bet-size-change.mp3";
import { playSound } from "../helpers/audio";
import { haptics } from "../helpers/haptics";
import { availableSpinBets, nextSpinBet } from "../helpers/spinBet";

const BANNER_DURATION_MS = 1500;

/** Initialize once when authoritative energy arrives; never clamp a user's choice. */
export function useBetMultiplier(
    energy: string | undefined,
    policy?: SmBettingPolicy,
    onLockedBet?: () => void,
) {
    const available = availableSpinBets(energy ?? "0", policy);
    const maximum = available[available.length - 1] ?? 1;
    const [selection, setSelection] = useState({
        multiplier: maximum,
        revision: 0,
        bannerDelay: 0,
        initialized: energy !== undefined && policy !== undefined,
    });
    const [isBannerVisible, setIsBannerVisible] = useState(false);

    if (
        !selection.initialized &&
        energy !== undefined &&
        policy !== undefined
    ) {
        setSelection({
            multiplier: maximum,
            revision: 0,
            bannerDelay: 0,
            initialized: true,
        });
    }

    const cycleMultiplier = useCallback(() => {
        if (available.length === 1) onLockedBet?.();
        const next = nextSpinBet(selection.multiplier, available);
        setIsBannerVisible(false);
        if (next === selection.multiplier) return;
        setSelection((current) => ({
            ...current,
            multiplier: nextSpinBet(current.multiplier, available),
            revision: current.revision + 1,
            bannerDelay: isBannerVisible ? 25 : 0,
        }));
        haptics.selectionChanged();
        playSound(betSizeChangeSound);
    }, [available, isBannerVisible, onLockedBet, selection.multiplier]);

    useEffect(() => {
        if (selection.revision === 0) return;
        let hideTimer: number | undefined;
        const showTimer = window.setTimeout(() => {
            setIsBannerVisible(true);
            hideTimer = window.setTimeout(
                () => setIsBannerVisible(false),
                BANNER_DURATION_MS,
            );
        }, selection.bannerDelay);
        return () => {
            window.clearTimeout(showTimer);
            window.clearTimeout(hideTimer);
        };
    }, [selection.bannerDelay, selection.revision]);

    return {
        multiplier: selection.multiplier,
        isMaxBet: available.length > 1 && selection.multiplier === maximum,
        available,
        isBannerVisible,
        cycleMultiplier,
    };
}
